import useFindAndReplaceBoards from './useFindAndReplaceBoards';
import { useCallback, useContext } from 'react';
import useFindAndReplaceGoals from './useFindAndReplaceGoals';
import useFindAndReplaceDashboardGadgets from './useFindAndReplaceDashboardGadgets';
import useFindAndReplaceChartDefinitions from './useFindAndReplaceChartDefinitions';
import useFindAndReplaceScorecards from './useFindAndReplaceScorecards';
import useArchiveUnusedMetric from './useArchiveUnusedMetric';
import STORE from '../../../../../../store';
import AccountPickerContext from '../../../../../../contexts/AccountPickerContext';
import getUsageCount from './getUsageCount';
import { FindAndReplace } from '../../types';
import useFindAndReplaceCompoundMetrics from './useFindAndReplaceCompoundMetrics';

const useFindAndReplace = () => {
  const { selectedAccountId } = useContext(AccountPickerContext);

  const findAndReplaceBoards = useFindAndReplaceBoards();
  const findAndReplaceGoals = useFindAndReplaceGoals();
  const findAndReplaceDashboardGadgets = useFindAndReplaceDashboardGadgets();
  const findAndReplaceChartDefinitions = useFindAndReplaceChartDefinitions();
  const findAndReplaceScorecards = useFindAndReplaceScorecards();
  const findAndReplaceCompoundMetrics = useFindAndReplaceCompoundMetrics();
  const archiveUnusedMetric = useArchiveUnusedMetric();

  const runFindAndReplace = useCallback(
    async (
      metricReceipt: FindAndReplace.MetricReceipt | undefined,
      archiveMetricIfUnused: boolean,
    ) => {
      if (!metricReceipt) {
        return;
      }

      const usageCount = getUsageCount(metricReceipt.affectedContentIds);
      if (usageCount !== 0) {
        await STORE.getFindAndReplaceReceiptsRef({
          accountId: selectedAccountId,
        })
          .doc(metricReceipt.id)
          .set(metricReceipt);

        await findAndReplaceBoards(metricReceipt);
        await findAndReplaceGoals(metricReceipt);
        await findAndReplaceDashboardGadgets(metricReceipt);
        await findAndReplaceChartDefinitions(metricReceipt);
        await findAndReplaceScorecards(metricReceipt);
        await findAndReplaceCompoundMetrics(metricReceipt);
      }

      if (archiveMetricIfUnused) {
        await archiveUnusedMetric(metricReceipt);
      }
    },
    [
      archiveUnusedMetric,
      findAndReplaceBoards,
      findAndReplaceChartDefinitions,
      findAndReplaceCompoundMetrics,
      findAndReplaceDashboardGadgets,
      findAndReplaceGoals,
      findAndReplaceScorecards,
      selectedAccountId,
    ],
  );

  return runFindAndReplace;
};

export default useFindAndReplace;
